import type { PrefillRejectReason } from '@/blocks/active-form/types';

/**
 * Longest proposed value rendered in the card before eliding.
 *
 * The gateway already size-caps the event, so this is not the safety boundary —
 * it keeps one long textarea draft from burying the Apply button in a narrow
 * side panel. The full value is still what gets written on Apply; only the
 * preview is cut.
 */
const MAX_DISPLAY_LENGTH = 600;

/**
 * Render a proposed value for display in `ProposalCard`, or `null` when there
 * is nothing to show.
 *
 * The wire type is `unknown` even though the agent sends strings today
 * (`AcceptedField.value` is a string in `services/ai-agent`), because the store
 * types the event payload defensively. This keeps the card honest for whatever
 * actually arrives rather than assuming a string and rendering `[object Object]`
 * — the user is about to write these values into their form, so a value they
 * cannot read is worse than no proposal.
 *
 * Returning `null` rather than a placeholder keeps this function free of
 * user-facing copy: the caller decides how to say "nothing here" through i18n.
 *
 * Note this is display only. The authoritative coercion happens in
 * `applyPrefill` against the real field schema (AID-377).
 */
export const formatProposalValue = (value: unknown): string | null => {
  const rendered = renderValue(value);

  if (rendered === null) {
    return null;
  }

  return rendered.length > MAX_DISPLAY_LENGTH
    ? `${rendered.slice(0, MAX_DISPLAY_LENGTH)}…`
    : rendered;
};

const renderValue = (value: unknown): string | null => {
  if (typeof value === 'string') {
    // A blank string is "no value", not a value that happens to be empty: the
    // card would otherwise render an empty row where the placeholder belongs.
    // Whitespace-only counts as blank for the same reason, but a non-blank
    // string keeps its original spacing — leading newlines can be meaningful in
    // a drafted textarea value.
    return value.trim() === '' ? null : value;
  }
  // Covers number and boolean. `null`/`undefined` fall through below rather
  // than stringifying to "null"/"undefined".
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value === null || value === undefined) {
    return null;
  }

  try {
    return JSON.stringify(value) ?? null;
  } catch {
    // Circular structures — unreachable over the wire (the payload is JSON), but
    // `unknown` does not promise that.
    return null;
  }
};

/**
 * i18n keys for the reasons `applyPrefill` refuses to write a field.
 *
 * A key map rather than a template literal at the call site so the keys stay
 * greppable, and typed by `PrefillRejectReason` so adding a reject reason is a
 * compile error until its copy exists.
 *
 * The copy deliberately explains the field, not the machinery: the user cannot
 * act on "unsupported_type", but they can understand that a field type is not
 * covered yet and fill it in themselves.
 */
export const REJECT_REASON_KEYS = {
  not_on_form: 'chat.prefill.rejectReason.notOnForm',
  unsupported_type: 'chat.prefill.rejectReason.unsupportedType',
  invalid_value: 'chat.prefill.rejectReason.invalidValue',
  // `as const` keeps the values as string LITERALS so `t()` accepts them — it is
  // typed against the union of real keys in common.json, so a widened `string`
  // is rejected and, better, a typo here fails to compile rather than rendering
  // the raw key path at the user. `satisfies` keeps the exhaustiveness check.
} as const satisfies Record<PrefillRejectReason, string>;
